import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import styled from "styled-components";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { fetchComunas, fetchRegiones } from "../services/register";

import { FloatLabel } from "primereact/floatlabel";
import { registerUser } from "../services/register";
import TextInput from "../components/TextInput";
import PasswordInput from "../components/passwordInput";
import { Fn } from "../utils/rutValidation";
import { isValidEmail } from "../utils/isValidEmail";
import Swal from "sweetalert2";

// Definición de colores centralizada
const colors = {
  primary: "#147abe", // Azul
  secondary: "#99c800", // Verde
  background: "#F4F4F4", // Gris claro para fondos
  textColor: "#2C3E50", // Gris oscuro para texto
  white: "#FFFFFF",
  error: "#dc3545", // Rojo para errores
  disabled: "#e9ecef", // Gris para elementos deshabilitados
  inputBorder: "#ced4da", // Borde para inputs
  shadow: "rgba(0, 0, 0, 0.1)", // Sombra estándar
};

// Componentes estilizados
export const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  border: 1px solid ${colors.inputBorder};
  border-radius: 10px;
  box-shadow: 0 0 10px ${colors.shadow};
`;

export const Title = styled.h1`
  text-align: center;
  margin-bottom: 20px;
  color: ${colors.textColor};
  font-size: 1.75rem;
`;

export const Label = styled.label`
  margin-top: 10px;
  display: block;
  font-weight: bold;
  color: ${colors.textColor};
  margin-bottom: 5px;
`;

export const StyledField = styled(Field)`
  width: 100%;
  padding: 10px;
  margin-top: 5px;
  margin-bottom: 20px;
  border: 1px solid ${colors.inputBorder};
  border-radius: 5px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 0.2rem rgba(20, 122, 190, 0.25);
  }

  &:disabled {
    background-color: ${colors.disabled};
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.div`
  color: ${colors.error};
  font-size: 14px;
  margin-top: -15px;
  margin-bottom: 15px;
  height: 20px; // Altura fija para evitar saltos de línea
`;

export const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;

  .form-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0;
    }
  }

  .field-container {
    width: 100%;
    margin-bottom: 1rem;
  }

  .full-width {
    width: 100%;
  }

  .location-fields {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0;
    }
  }

  .error-summary {
    background-color: #fff3cd;
    color: #856404;
    padding: 1rem;
    border-radius: 5px;
    margin-bottom: 1rem;
    border: 1px solid #ffeeba;

    ul {
      margin-top: 0.5rem;
      padding-left: 1.5rem;
    }
  }
`;

export const Card = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
`;

export const CustomDialog = styled(Dialog)`
  width: 40%;
  @media screen and (max-width: 800px) {
    width: 90%;
  }

  .p-dialog {
    width: 90%;
    max-width: 700px;
  }

  .p-dialog-header {
    background-color: transparent;
    border-bottom: none;
    display: none;
  }

  .dialog-content {
    display: flex;
    flex-direction: column;
    padding: 2rem;
    gap: 1rem;
    border-radius: 12px;
    background-color: ${colors.background};

    @media screen and (max-width: 800px) {
      padding: 1rem;
    }
  }

  .p-dialog-footer {
    display: none;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const ButtonRegister = styled.button`
  background-color: ${colors.primary};
  color: ${colors.white};
  border: none;
  padding: 1rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: ${colors.secondary};
    transform: translateY(-2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(20, 122, 190, 0.25);
  }
`;

export const ButtonSubmit = styled.button`
  background-color: ${colors.secondary};
  color: ${colors.white};
  border: none;
  padding: 1rem;
  width: 100%;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: ${colors.primary};
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: ${colors.disabled};
    cursor: not-allowed;
    transform: none;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(153, 200, 0, 0.25);
  }
`;

export const ButtonCancel = styled.button`
  background-color: ${colors.primary};
  color: ${colors.white};
  border: none;
  padding: 1rem;
  width: 100%;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: ${colors.secondary};
    transform: translateY(-2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(20, 122, 190, 0.25);
  }
`;

/// Fin estilos
const RegisterForm = () => {
  const [visible, setVisible] = useState(false);
  const [comunas, setComunas] = useState([]);
  const [regions, setRegions] = useState([]);
  const [formErrors, setFormErrors] = useState([]);

  // Fetch de regiones solo al montar el componente
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await fetchRegiones();
        setRegions(response || []);
      } catch (error) {
        console.error("Error al cargar regiones:", error);
      }
    };

    loadRegions();
  }, []);

  const initialValues = {
    rut: "",
    firstName: "",
    paternalLastName: "", // Corregido el nombre de la variable
    maternalLastName: "", // Corregido el nombre de la variable
    email: "",
    region: "",
    comuna: "",
    phone: "",
    password: "",
    confirmPassword: "",
  };

  // Handler para envío del formulario con manejo de errores
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const userData = {
        rut_usuario: values.rut,
        nombre_usuario: values.firstName,
        apellido_usuario:
          `${values.paternalLastName} ${values.maternalLastName}`.trim(),
        correo_usuario: values.email,
        id_comuna: values.comuna,
        numero_telefono: values.phone,
        contrasenna: values.password,
        rol: "Postulante",
        reset: true,
      };

      const response = await registerUser(userData);

      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Registro exitoso",
          text: "Se ha creado tu cuenta exitosamente",
          confirmButtonText: "Aceptar",
          confirmButtonColor: `${colors.primary}`,
          topLayer: true,
        });
        setVisible(false);
      } else {
        throw new Error("Error en el registro", response.data);
      }
    } catch (error) {
      console.error("Error al registrar usuario:", error.response?.data);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `${error.message}`,
        confirmButtonText: "Aceptar",
        confirmButtonColor: `${colors.primary}`,
        topLayer: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handler para cambio de región con manejo de errores
  const handleRegionChange = async (event, setFieldValue) => {
    const selectedRegionId = event.target.value;
    setFieldValue("region", selectedRegionId);

    // Resetear comuna cuando cambia la región
    setFieldValue("comuna", "");

    if (!selectedRegionId) {
      setComunas([]);
      return;
    }

    try {
      const response = await fetchComunas(selectedRegionId);
      setComunas(response?.comunas || []);
    } catch (error) {
      console.error("Error al cargar comunas:", error);
      setComunas([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las comunas. Intente nuevamente.",
        confirmButtonColor: "#147abe",
      });
    }
  };

  // Validación de RUT mejorada
  const validateRut = (rut) => {
    if (!rut) {
      return "RUT requerido";
    }

    if (!Fn.validaRut(rut)) {
      return "RUT inválido. Debe ingresarse sin puntos y con guion";
    }

    return null;
  };

  // Función de validación del formulario con mensajes de error mejorados
  const validateForm = (values) => {
    const errors = {};

    // Validación de RUT
    const rutError = validateRut(values.rut);
    if (rutError) errors.rut = rutError;

    // Validación de campos de texto
    if (!values.firstName) {
      errors.firstName = "Nombre requerido";
    } else if (values.firstName.length < 2) {
      errors.firstName = "El nombre debe tener al menos 2 caracteres";
    }

    if (!values.paternalLastName) {
      errors.paternalLastName = "Apellido paterno requerido";
    } else if (values.paternalLastName.length < 2) {
      errors.paternalLastName =
        "El apellido paterno debe tener al menos 2 caracteres";
    }

    if (!values.maternalLastName) {
      errors.maternalLastName = "Apellido materno requerido";
    } else if (values.maternalLastName.length < 2) {
      errors.maternalLastName =
        "El apellido materno debe tener al menos 2 caracteres";
    }

    // Validación de email
    if (!values.email) {
      errors.email = "Correo electrónico requerido";
    } else if (!isValidEmail(values.email)) {
      errors.email = "Formato de correo electrónico inválido";
    }

    // Validación de región y comuna
    if (!values.region) {
      errors.region = "Seleccione una región";
    }

    if (!values.comuna) {
      errors.comuna = "Seleccione una comuna";
    }

    // Validación de teléfono
    if (!values.phone) {
      errors.phone = "Teléfono requerido";
    } else if (values.phone.length < 8) {
      errors.phone = "El teléfono debe tener al menos 8 dígitos";
    }

    // Validación de contraseña
    if (!values.password) {
      errors.password = "Contraseña requerida";
    } else if (values.password.length < 8) {
      errors.password = "La contraseña debe tener al menos 8 caracteres";
    }

    if (!values.confirmPassword) {
      errors.confirmPassword = "Confirmación de contraseña requerida";
    } else if (values.confirmPassword !== values.password) {
      errors.confirmPassword = "Las contraseñas no coinciden";
    }

    // Actualizar errores para mostrarlos
    setFormErrors(Object.values(errors));

    return errors;
  };

  return (
    <Card className="register-card">
      <ButtonRegister
        onClick={() => setVisible(true)}
        aria-label="Abrir formulario de registro"
      >
        ¿No tienes cuenta?
      </ButtonRegister>

      <CustomDialog
        visible={visible}
        modal
        onHide={() => setVisible(false)}
        aria-labelledby="registro-titulo"
      >
        <br />
        <div className="dialog-content">
          <Title id="registro-titulo">Registro de usuario</Title>

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validate={validateForm}
          >
            {({ setFieldValue, values, isSubmitting }) => (
              <StyledForm as={Form} noValidate>
                {/* Fila 1: RUT y Nombre */}
                <div className="form-row">
                  <FloatLabel className="field-container">
                    <TextInput
                      maxLength="12"
                      type="text"
                      id="rut"
                      name="rut"
                      aria-label="RUT"
                      aria-required="true"
                      onChange={(event) => {
                        const raw = event.target.value.replace(/[^0-9kK]/g, "");
                        const formatted = Fn.formatRut(raw);
                        setFieldValue("rut", formatted);
                      }}
                    />
                    <label htmlFor="rut">RUT *</label>
                  </FloatLabel>

                  <FloatLabel className="field-container">
                    <TextInput
                      type="text"
                      id="firstName"
                      name="firstName"
                      aria-label="Nombre"
                      aria-required="true"
                      onChange={(event) => {
                        setFieldValue("firstName", event.target.value);
                      }}
                    />
                    <label htmlFor="firstName">Nombre *</label>
                  </FloatLabel>
                </div>

                {/* Fila 2: Apellidos */}
                <div className="form-row">
                  <FloatLabel className="field-container">
                    <TextInput
                      type="text"
                      id="paternalLastName"
                      name="paternalLastName"
                      aria-label="Apellido paterno"
                      aria-required="true"
                      onChange={(event) => {
                        setFieldValue("paternalLastName", event.target.value);
                      }}
                    />
                    <label htmlFor="paternalLastName">Apellido paterno *</label>
                  </FloatLabel>

                  <FloatLabel className="field-container">
                    <TextInput
                      type="text"
                      id="maternalLastName"
                      name="maternalLastName"
                      aria-label="Apellido materno"
                      aria-required="true"
                      onChange={(event) => {
                        setFieldValue("maternalLastName", event.target.value);
                      }}
                    />
                    <label htmlFor="maternalLastName">Apellido materno *</label>
                  </FloatLabel>
                </div>

                {/* Correo electrónico */}
                <FloatLabel className="field-container full-width">
                  <TextInput
                    type="email"
                    id="email"
                    name="email"
                    aria-label="Correo electrónico"
                    aria-required="true"
                    onChange={(event) => {
                      setFieldValue("email", event.target.value);
                    }}
                  />
                  <label htmlFor="email">Correo electrónico *</label>
                </FloatLabel>

                {/* Región y Comuna */}
                <div className="location-fields">
                  <div className="field-container">
                    <Label htmlFor="region">Región: *</Label>
                    <StyledField
                      as="select"
                      id="region"
                      name="region"
                      aria-label="Región"
                      aria-required="true"
                      onChange={(event) =>
                        handleRegionChange(event, setFieldValue)
                      }
                    >
                      <option value="">Seleccione una región</option>
                      {regions.map((region) => (
                        <option key={region.id_region} value={region.id_region}>
                          {region.descripcion_region}
                        </option>
                      ))}
                    </StyledField>
                    <ErrorMessage name="region" component={ErrorText} />
                  </div>

                  <div className="field-container">
                    <Label htmlFor="comuna">Comuna: *</Label>
                    <StyledField
                      as="select"
                      id="comuna"
                      name="comuna"
                      aria-label="Comuna"
                      aria-required="true"
                      disabled={!values.region}
                      onChange={(event) =>
                        setFieldValue("comuna", event.target.value)
                      }
                    >
                      <option value="">
                        {values.region
                          ? "Seleccione una comuna"
                          : "Primero seleccione una región"}
                      </option>
                      {comunas.map((comuna) => (
                        <option key={comuna.id_comuna} value={comuna.id_comuna}>
                          {comuna.descripcion_comuna}
                        </option>
                      ))}
                    </StyledField>
                    <ErrorMessage name="comuna" component={ErrorText} />
                  </div>
                </div>

                {/* Teléfono */}
                <FloatLabel className="field-container full-width">
                  <TextInput
                    type="tel"
                    id="phone"
                    name="phone"
                    aria-label="Teléfono"
                    aria-required="true"
                    onChange={(event) => {
                      setFieldValue("phone", event.target.value);
                    }}
                  />
                  <label htmlFor="phone">Teléfono *</label>
                </FloatLabel>

                {/* Contraseña */}
                <FloatLabel className="field-container full-width">
                  <TextInput
                    type="password"
                    id="password"
                    name="password"
                    component={PasswordInput}
                    aria-label="Contraseña"
                    aria-required="true"
                    onChange={(event) => {
                      setFieldValue("password", event.target.value);
                    }}
                  />
                  <label htmlFor="password">Contraseña *</label>
                </FloatLabel>

                {/* Confirmar Contraseña */}
                <FloatLabel className="field-container full-width">
                  <TextInput
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    component={PasswordInput}
                    aria-label="Confirmar contraseña"
                    aria-required="true"
                    onChange={(event) => {
                      setFieldValue("confirmPassword", event.target.value);
                    }}
                  />
                  <label htmlFor="confirmPassword">
                    Confirmar Contraseña *
                  </label>
                </FloatLabel>

                {/* Botones de acción */}
                <ButtonContainer>
                  <ButtonSubmit
                    type="submit"
                    disabled={isSubmitting}
                    aria-label="Crear cuenta"
                  >
                    {isSubmitting ? "Procesando..." : "Crear cuenta"}
                  </ButtonSubmit>
                  <ButtonCancel
                    type="button"
                    onClick={() => setVisible(false)}
                    aria-label="Cancelar registro"
                  >
                    Cancelar
                  </ButtonCancel>
                </ButtonContainer>
              </StyledForm>
            )}
          </Formik>
        </div>
      </CustomDialog>
    </Card>
  );
};

export default RegisterForm;
